import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { opsAPI, opsSubscribe } from "./opsApi";
import CountUp from "@/pages/operations/CountUp";
import EventCard from "./EventCard";
import AIOpsWidget from "./AIOpsWidget";
import {
  Bell, AlertTriangle, ListChecks, ClipboardCheck, AtSign, Radio,
  ShieldAlert, ArrowUpCircle, Star, Users, ArrowRight, Rss,
} from "lucide-react";

const Tile = ({ label, value, icon: Icon, accent = "text-hocp-gold", testid, to }) => {
  const body = (
    <div data-testid={testid}
      className="on-dark relative overflow-hidden bg-gradient-to-br from-[#0F1530] to-[#070B1F] rounded-2xl p-5 border border-white/10">
      <div className="absolute -top-6 -right-6 w-28 h-28 rounded-full bg-hocp-gold/5 blur-2xl pointer-events-none"/>
      <div className={`w-10 h-10 rounded-xl bg-white/10 ring-1 ring-white/15 flex items-center justify-center ${accent} relative`}>
        <Icon className="w-5 h-5" strokeWidth={1.75}/>
      </div>
      <div className="mt-4 relative">
        <div className="font-display text-[26px] font-semibold text-white tabular-nums leading-none">{value}</div>
        <div className="text-[11px] text-slate-200 font-medium mt-2 uppercase tracking-wider">{label}</div>
      </div>
    </div>
  );
  return to ? <Link to={to} className="block">{body}</Link> : body;
};

export default function OpsDashboard() {
  const [d, setD] = useState(null);
  const [feed, setFeed] = useState([]);
  const [live, setLive] = useState(0);

  useEffect(() => {
    const load = () => {
      opsAPI.dashboard().then(setD).catch(()=>{});
      opsAPI.feed({ limit: 12 }).then(setFeed).catch(()=>{});
    };
    load();
    const iv = setInterval(load, 30000);
    const unsub = opsSubscribe(payload => {
      if (payload.type === "event") {
        setFeed(prev => [payload.event, ...prev].slice(0, 12));
        setLive(x => x + 1);
      }
    });
    return () => { clearInterval(iv); unsub(); };
  }, []);

  const k = d?.kpis || {}; const pb = d?.priority_breakdown || {};

  return (
    <div className="space-y-5" data-testid="ops-dashboard">
      {/* Hero */}
      <div className="on-dark relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0B1020] via-[#070B1F] to-[#0F1530] p-6 border border-white/10">
        <div className="absolute -top-10 -right-10 w-64 h-64 rounded-full bg-hocp-gold/10 blur-3xl pointer-events-none"/>
        <div className="text-[11px] uppercase tracking-widest text-slate-300 font-medium flex items-center gap-2">
          <Bell className="w-3.5 h-3.5"/> Operations Hub
          <span className="text-hocp-gold font-semibold tabular-nums">{d?.business_date || "—"}</span>
          <span className="ml-auto inline-flex items-center gap-1.5 text-[10.5px] text-emerald-300" data-testid="ops-live-indicator">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"/> LIVE
            {live > 0 && <span className="mono">· +{live} new</span>}
          </span>
        </div>
        <div className="mt-3 font-display text-[42px] font-bold text-white tabular-nums leading-none">
          <CountUp value={Number(k.unread)||0}/>
          <span className="text-slate-400 text-[22px]"> unread signals</span>
        </div>
        <div className="text-[13px] text-slate-300 mt-2">
          {k.critical || 0} critical · {k.pending_tasks || 0} open tasks · {k.approvals_waiting || 0} approvals waiting
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Tile label="Unread"            value={<CountUp value={Number(k.unread)||0}/>}          icon={Bell}          testid="ops-kpi-unread"  to="/operations-hub/notifications" accent="text-sky-300"/>
        <Tile label="Critical"          value={<CountUp value={Number(k.critical)||0}/>}        icon={AlertTriangle} testid="ops-kpi-critical" to="/operations-hub/notifications?priority=critical" accent="text-rose-300"/>
        <Tile label="Pending Tasks"     value={<CountUp value={Number(k.pending_tasks)||0}/>}   icon={ListChecks}    testid="ops-kpi-tasks"    to="/operations-hub/tasks" accent="text-amber-300"/>
        <Tile label="Approvals Waiting" value={<CountUp value={Number(k.approvals_waiting)||0}/>} icon={ClipboardCheck} testid="ops-kpi-approvals" to="/workflow/inbox" accent="text-fuchsia-300"/>
        <Tile label="Mentions"          value={<CountUp value={Number(k.mentions)||0}/>}         icon={AtSign}         testid="ops-kpi-mentions" to="/operations-hub/mentions" accent="text-cyan-300"/>
        <Tile label="Broadcasts Today"  value={<CountUp value={Number(k.broadcasts_today)||0}/>} icon={Radio}          testid="ops-kpi-broadcasts" to="/operations-hub/broadcasts" accent="text-emerald-300"/>
        <Tile label="System Alerts"     value={<CountUp value={Number(k.system_alerts)||0}/>}    icon={ShieldAlert}    testid="ops-kpi-system"    to="/operations-hub/feed?module=sys" accent="text-orange-300"/>
        <Tile label="Escalations"       value={<CountUp value={Number(k.escalations)||0}/>}      icon={ArrowUpCircle}  testid="ops-kpi-escalations" to="/workflow/escalations" accent="text-amber-300"/>
        <Tile label="VIP Guests Today"  value={<CountUp value={Number(k.vip_today)||0}/>}        icon={Star}           testid="ops-kpi-vip" accent="text-hocp-gold"/>
        <Tile label="Staff Online"      value={<CountUp value={Number(k.staff_online)||0}/>}     icon={Users}          testid="ops-kpi-staff" accent="text-teal-300"/>
      </div>

      {/* Feed + priority breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="ops-recent-feed">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
            <Rss className="w-4 h-4 text-hocp-navy"/>
            <div className="font-display text-[14px] font-semibold text-hocp-navy">Latest 12 Events</div>
            <Link to="/operations-hub/feed" className="ml-auto inline-flex items-center gap-1 text-[12px] text-slate-500 hover:text-hocp-navy">
              View full feed <ArrowRight className="w-3.5 h-3.5"/>
            </Link>
          </div>
          <div className="divide-y divide-slate-100 max-h-[560px] overflow-y-auto">
            {feed.length === 0
              ? <div className="p-8 text-center text-[13px] text-slate-400">No events yet.</div>
              : feed.map(e => <EventCard key={e.id} e={e} compact testid={`ops-feed-item-${e.id}`}/>)}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="ops-priority-breakdown">
          <div className="px-4 py-3 border-b border-slate-100 font-display text-[14px] font-semibold text-hocp-navy">Signal Volume by Priority</div>
          <div className="p-4 space-y-3">
            {[
              { k: "critical", label: "Critical", cls: "bg-rose-400" },
              { k: "high",     label: "High",     cls: "bg-amber-400" },
              { k: "normal",   label: "Normal",   cls: "bg-sky-300" },
              { k: "low",      label: "Low",      cls: "bg-slate-300" },
            ].map(row => {
              const count = pb[row.k] || 0;
              const max = Math.max(1, Object.values(pb).reduce((a,b)=>Math.max(a,b), 1));
              return (
                <div key={row.k} data-testid={`ops-pb-${row.k}`}>
                  <div className="flex items-center justify-between text-[12.5px] mb-1">
                    <span className="text-hocp-navy font-medium">{row.label}</span>
                    <span className="mono tabular-nums text-slate-500">{count}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className={`h-full ${row.cls}`} style={{ width: `${Math.max(4, (count/max)*100)}%` }}/>
                  </div>
                </div>
              );
            })}
          </div>
          {/* AI Ops widget lives inside the right column, under the priority breakdown */}
          <div className="p-3 pt-0">
            <AIOpsWidget/>
          </div>
        </div>
      </div>
    </div>
  );
}
